import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Group,
  Stack,
  Title,
  Paper,
  Tabs,
  Menu,
  Anchor,
  Text,
} from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { IconPlus } from '@tabler/icons-react';

import type { Contract, ContractType } from '../../models/contract.ts';
import { CONTRACT_TYPE_LABEL, PROFILE_TYPE_LABEL } from '../../const/labels.ts';

import { formatDateTime2 } from '../../utils/DateUtil.ts';

const CONTRACT_PATH: { [key in ContractType]: string } = {
  ParkingContract: 'parking-contract',
  RefuelingContract: 'refueling-contract',
  ChainStoreContract: 'chain-store-contract',
};

type TabState = 'PENDING' | 'ACTIVE' | 'EXPIRED';

interface ContractTableProps {
  partnerId: string;
  contracts: Contract[];
  isPending: boolean;
}

export default function ContractTable({
  partnerId,
  contracts,
  isPending,
}: ContractTableProps) {
  const [tabState, setTabState] = useState<TabState>('ACTIVE');
  const navigate = useNavigate();

  const activeContracts = contracts.filter((contract) => {
    return contract.state === 'ACTIVE';
  });
  const pendingContracts = contracts.filter((contract) => {
    return ['DRAFT', 'PENDING'].includes(contract.state);
  });
  const expiredContracts = contracts.filter((contract) => {
    return contract.state === 'EXPIRED';
  });
  const filteredContracts = () => {
    switch (tabState) {
      case 'ACTIVE':
        return activeContracts;
      case 'PENDING':
        return pendingContracts;
      case 'EXPIRED':
        return expiredContracts;
      default:
        return [];
    }
  };
  const noRecordText: () => string = () => {
    switch (tabState) {
      case 'ACTIVE':
        return '目前沒有執行中的合約';
      case 'PENDING':
        return '目前沒有待執行的合約';
      case 'EXPIRED':
        return '目前沒有已到期的合約';
    }
  };

  const columns: DataTableColumn<Contract>[] = [
    {
      title: '合約名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      width: 600,
      accessor: 'name',
      render: (row) => {
        const base = `/partners/${partnerId}/${CONTRACT_PATH[row.type]}s/${row.id}`;
        const link = row.state === 'DRAFT' ? `${base}/edit` : base;
        return (
          <Anchor href={link}>
            <Text fz={14} td="underline">
              {row.name}
            </Text>
          </Anchor>
        );
      },
    },
    {
      title: '合約類型',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'type',
      render: (row) => CONTRACT_TYPE_LABEL[row.type],
    },
    {
      title: '合約套用方式',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'profileType',
      render: (row) => {
        if (row.type === 'RefuelingContract') {
          return '-'; // 加油合約不分合約套用方式
        }

        return PROFILE_TYPE_LABEL[row.profileType];
      },
    },
    {
      title: '合約起迄時間',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: '',
      render: (row) => {
        if (!row.startAt && !row.endAt) {
          return <>-</>;
        }

        const startAt = formatDateTime2(row.startAt);
        const endAt = formatDateTime2(row.endAt);
        return (
          <Group>
            <Text fz={14} w={120}>
              {startAt}
            </Text>
            <Text>-</Text>
            <Text fz={14} w={120}>
              {endAt}
            </Text>
          </Group>
        );
      },
    },
  ];

  return (
    <>
      <Paper radius="lg" shadow="xs">
        <Group justify="space-between" p="xl">
          <Title order={3}>合約管理</Title>
          <Menu shadow="md" width={160}>
            <Menu.Target>
              <Button leftSection={<IconPlus size={20} />}>新增合約</Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  navigate(`/partners/${partnerId}/parking-contracts/new`)
                }
              >
                停車場合約
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  navigate(`/partners/${partnerId}/refueling-contracts/new`)
                }
              >
                加油站合約
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  navigate(`/partners/${partnerId}/chain-store-contracts/new`)
                }
              >
                連鎖店合約
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Stack px="md" pb="xl">
          <Tabs defaultValue="ACTIVE">
            <Tabs.List>
              <Tabs.Tab
                value="ACTIVE"
                onClick={() => setTabState('ACTIVE')}
              >{`執行中合約 (${activeContracts.length})`}</Tabs.Tab>
              <Tabs.Tab
                value="PENDING"
                onClick={() => setTabState('PENDING')}
              >{`待執行/建立中合約 (${pendingContracts.length})`}</Tabs.Tab>
              <Tabs.Tab
                value="EXPIRED"
                onClick={() => setTabState('EXPIRED')}
              >{`已到期合約 (${expiredContracts.length})`}</Tabs.Tab>
            </Tabs.List>
          </Tabs>
          <DataTable
            idAccessor="id"
            highlightOnHover
            minHeight={150}
            columns={columns}
            records={filteredContracts()}
            fetching={isPending}
            verticalSpacing="sm"
            noRecordsText={noRecordText()}
          />
        </Stack>
      </Paper>
    </>
  );
}
